'use client';
import React, { useState } from 'react';
import StatusBadge from '@/components/StatusBadge';
import { Search } from 'lucide-react';

const retentionCustomers = [
  { id: 'rc-001', customer: 'Hoffman, B.', address: 'Laguna Niguel', lastService: '14 months', value: '$3,400', campaign: 'Stage 3', probability: 74, nextAction: 'Offer call scheduled', status: 'Engaged' },
  { id: 'rc-002', customer: 'Nguyen, P.', address: 'Garden Grove', lastService: '18 months', value: '$2,800', campaign: 'Stage 2', probability: 61, nextAction: 'SMS coupon sent', status: 'Responding' },
  { id: 'rc-003', customer: 'Davis, K.', address: 'Fullerton', lastService: '22 months', value: '$5,200', campaign: 'Stage 4', probability: 48, nextAction: 'Final outreach call', status: 'At Risk' },
  { id: 'rc-004', customer: 'Thompson, S.', address: 'Mission Viejo', lastService: '11 months', value: '$4,100', campaign: 'Stage 1', probability: 82, nextAction: 'Membership offer email', status: 'Engaged' },
  { id: 'rc-005', customer: 'Wilson, C.', address: 'Brea', lastService: '16 months', value: '$3,900', campaign: 'Stage 3', probability: 66, nextAction: 'Estimate follow-up', status: 'Responding' },
  { id: 'rc-006', customer: 'Moore, A.', address: 'Yorba Linda', lastService: '28 months', value: '$6,800', campaign: 'Stage 5', probability: 31, nextAction: 'Last chance SMS', status: 'Cold' },
  { id: 'rc-007', customer: 'Jackson, T.', address: 'Huntington Beach', lastService: '13 months', value: '$2,200', campaign: 'Stage 2', probability: 71, nextAction: 'Tune-up appointment offer', status: 'Engaged' },
  { id: 'rc-008', customer: 'White, M.', address: 'Placentia', lastService: '20 months', value: '$4,400', campaign: 'Stage 4', probability: 44, nextAction: 'Discount offer call', status: 'At Risk' },
  { id: 'rc-009', customer: 'Harris, J.', address: 'Stanton', lastService: '15 months', value: '$3,100', campaign: 'Stage 3', probability: 58, nextAction: 'Email sequence active', status: 'Responding' },
  { id: 'rc-010', customer: 'Martin, L.', address: 'Los Alamitos', lastService: '31 months', value: '$7,200', campaign: 'Stage 5', probability: 22, nextAction: 'Reactivation mailer', status: 'Cold' },
];

const statusVariant: Record<string, 'active' | 'pending' | 'warning' | 'error' | 'idle'> = {
  'Engaged': 'active',
  'Responding': 'pending',
  'At Risk': 'warning',
  'Cold': 'idle',
};

export default function RetentionEngineTable() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');

  const statuses = ['All', 'Engaged', 'Responding', 'At Risk', 'Cold'];

  const filtered = retentionCustomers.filter(c =>
    (filter === 'All' || c.status === filter) &&
    c.customer.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="bg-card rounded-lg card-shadow border border-border">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Customer Retention Engine</h3>
          <p className="text-xs text-muted-foreground mt-0.5">$2.4M dormant pool · $61,800 recovered this month</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search customers..."
              className="pl-8 pr-3 py-1.5 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring w-44"
            />
          </div>
          <div className="flex items-center gap-1">
            {statuses.map(s => (
              <button
                key={`ret-filter-${s}`}
                onClick={() => setFilter(s)}
                className={`text-xs px-2 py-1 rounded-md transition-colors ${filter === s ? 'bg-secondary text-white' : 'text-muted-foreground hover:bg-muted'}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-xs min-w-max">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Customer</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Last Service</th>
              <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Value</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Campaign Stage</th>
              <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Probability</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Next Action</th>
              <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                <td className="px-4 py-2.5">
                  <div className="font-medium text-foreground">{c.customer}</div>
                  <div className="text-muted-foreground">{c.address}</div>
                </td>
                <td className="px-3 py-2.5 text-muted-foreground font-tabular">{c.lastService}</td>
                <td className="px-3 py-2.5 text-right font-semibold font-tabular text-foreground">{c.value}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{c.campaign}</td>
                <td className="px-3 py-2.5 text-right">
                  <span className={`font-semibold font-tabular text-xs ${c.probability >= 70 ? 'text-success' : c.probability >= 50 ? 'text-warning' : 'text-danger'}`}>{c.probability}%</span>
                </td>
                <td className="px-3 py-2.5 text-muted-foreground max-w-[180px] truncate">{c.nextAction}</td>
                <td className="px-3 py-2.5">
                  <StatusBadge variant={statusVariant[c.status] ?? 'idle'} label={c.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
        <span>Showing {filtered.length} of {retentionCustomers.length} customers</span>
        <span className="font-semibold text-success">42 reactivations · 28 memberships restored this month</span>
      </div>
    </div>
  );
}